import _ from 'lodash';
import Logger from 'logger';
import { API_V1 } from './APIs/APIv1';
import {
  WebmeticsMapping,
  ApiResponse,
} from '../features/lead-list-research/webmetics/webmetics.types';

/**
 * Fetch company information from Webmetics.
 * Expects a domain parameter and optional query parameters.
 */
export const getCompanyInfo = async (
  domain: string,
  params?: { from_date?: string; to_date?: string; page?: number; page_size?: number }
): Promise<ApiResponse<any>> => {
  try {
    const response = await API_V1.get('/webmetics/company', {
      params: { domain, ...params },
    });
    return { data: response.data, error: null };
  } catch (e: any) {
    Logger.trace(`Error while fetching company info for domain ${domain}!`);
    Logger.error(e);
    return { data: null, error: _.get(e, 'response.data', e) };
  }
};

/**
 * Fetch company sessions from Webmetics.
 * Expects a domain parameter and optional query parameters.
 */
export const getCompanySessions = async (
  domain: string,
  params?: { from_date?: string; to_date?: string; page?: number; page_size?: number }
): Promise<ApiResponse<any>> => {
  try {
    const response = await API_V1.get('/webmetics/company-sessions', {
      params: { domain, ...params },
    });
    return { data: response.data, error: null };
  } catch (e: any) {
    Logger.trace(`Error while fetching company sessions for domain ${domain}!`);
    Logger.error(e);
    return { data: null, error: _.get(e, 'response.data', e) };
  }
};

/**
 * Create a new Webmetics mapping.
 */
export const createWebmeticsMapping = async (
  submission: Partial<WebmeticsMapping>
): Promise<ApiResponse<WebmeticsMapping>> => {
  try {
    const response = await API_V1.post('/webmetics', submission, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return { data: response.data, error: null };
  } catch (error: any) {
    Logger.trace('Error creating Webmetics mapping!');
    Logger.error(error);
    return { data: null, error: _.get(error, 'response.data', error) };
  }
};

/**
 * Update an existing Webmetics mapping.
 */
export const updateWebmeticsMapping = async (
  mappingId: string,
  submission: Partial<WebmeticsMapping>
): Promise<ApiResponse<WebmeticsMapping>> => {
  try {
    const response = await API_V1.put(`/webmetics/${mappingId}`, submission, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return { data: response.data, error: null };
  } catch (error: any) {
    Logger.trace(`Error updating Webmetics mapping ${mappingId}!`);
    Logger.error(error);
    return { data: null, error: _.get(error, 'response.data', error) };
  }
};

/**
 * Save a Webmetics mapping.
 * This helper function will determine whether to create a new mapping or update an existing one
 * based on the presence of an "id" in the mapping object.
 */
export const saveWebmeticsMapping = async (
  mapping: Partial<WebmeticsMapping>
): Promise<ApiResponse<WebmeticsMapping>> => {
  if (mapping._id) {
    return updateWebmeticsMapping(mapping._id, mapping);
  } else {
    return createWebmeticsMapping(mapping);
  }
};

/**
 * Delete a Webmetics mapping.
 */
export const deleteWebmeticsMapping = async (
  mappingId: string
): Promise<ApiResponse<null>> => {
  try {
    const response = await API_V1.delete(`/webmetics/${mappingId}`);
    return { data: response.data, error: null };
  } catch (error: any) {
    Logger.trace(`Error deleting Webmetics mapping ${mappingId}!`);
    Logger.error(error);
    return { data: null, error: _.get(error, 'response.data', error) };
  }
};

/**
 * Sync a Webmetics mapping.
 * This will update the mapping with the latest company info from Webmetics,
 * and trigger search creation on the backend.
 */
export const syncWebmeticsMapping = async (
  mappingId: string
): Promise<ApiResponse<any>> => {
  try {
    const response = await API_V1.post(`/webmetics/${mappingId}/sync`, {});
    return { data: response.data, error: null };
  } catch (error: any) {
    Logger.trace(`Error syncing Webmetics mapping ${mappingId}!`);
    Logger.error(error);
    return { data: null, error: _.get(error, 'response.data', error) };
  }
};

/**
 * Get all Webmetics mappings.
 * Supports optional pagination via page (offset multiplier) and total (limit).
 */
export const getAllWebmeticsMappings = async (
  page?: number,
  total?: number
): Promise<ApiResponse<WebmeticsMapping[]>> => {
  try {
    const params: any = {};
    if (!_.isNil(page) && !_.isNil(total)) {
      params.limit = total;
      params.offset = page * total;
    }
    const response = await API_V1.get('/webmetics/', { params });
    return { data: response.data, error: null };
  } catch (error: any) {
    Logger.trace('Error fetching Webmetics mappings!');
    Logger.error(error);
    return { data: null, error: _.get(error, 'response.data', error) };
  }
};

export const testWebmeticsMapping = async (
  mappingId: string
): Promise<ApiResponse<any>> => {
  try {
    const response = await API_V1.get(`/webmetics/${mappingId}/test`);
    return { data: response.data, error: null };
  } catch (error: any) {
    Logger.trace(`Error testing Webmetics mapping ${mappingId}!`);
    Logger.error(error);
    return { data: null, error: _.get(error, 'response.data', error) };
  }
};
